"use client"

import { useState, useEffect, useRef } from "react"
import "./AIProcess.css"

const ProductDesignProcess = () => {
  const [activeTab, setActiveTab] = useState("research") // Default to 'research' tab
  const headerTitleRef = useRef(null)
  const topParagraphRef = useRef(null)
  const mainDescriptionRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    research: {
      title: "RESEARCH",
      steps: ["1. BRIEFING", "2. BENCHMARK", "3. DATA RESEARCH","4. PROBLEM DEFINITION"],
      paragraphs: [
        "Our AI process starts with a deep dive into business vision, researching existing solutions and available data to allow for a clear problem definition",
      ],
    },
    ideation: {
      title: "IDEATION",
      steps: ["1. EXPLORATORY ANALYSIS", "2. PREDICTIVE ANALYSIS", "3. PROOF OF CONCEPT","4. VIABILITY ASSESMENT"],
      paragraphs: [
        "It is followed by a comprehensive analysis of the gathered data and ideation of possible solutions, leading to a proof of concept and viability assessment.",
      ],
    },
    execution: {
      title: "EXECUTION",
      steps: ["1. MLOPS BOOTSTRAP ","2. PRODUCTION INCREMENT"],
      paragraphs: [
        "After the prototyped solution is integrated into the product, a streamlined MLOps is set in place to run quick experiments and production increments.",
      ],
    },
    technical_assessment: {
      title: "TECHNICAL ASSESSMENT",
      steps: ["1. HIGH-LEVEL ARCHITECTURE","2. PROJECT PLAN"],
      paragraphs: [
        "Throughout the whole process, the high-level architecture of the AI solution and the Project Plan are designed and refined, accounting for all requirements, dependencies, costs and risks.",
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 },
    )

    if (headerTitleRef.current) observer.observe(headerTitleRef.current)
    if (topParagraphRef.current) observer.observe(topParagraphRef.current)
    if (mainDescriptionRef.current) observer.observe(mainDescriptionRef.current)
    if (imageRef.current) observer.observe(imageRef.current)
    if (tabsRef.current) observer.observe(tabsRef.current)

    return () => {
      if (headerTitleRef.current) observer.unobserve(headerTitleRef.current)
      if (topParagraphRef.current) observer.unobserve(topParagraphRef.current)
      if (mainDescriptionRef.current) observer.unobserve(mainDescriptionRef.current)
      if (imageRef.current) observer.unobserve(imageRef.current)
      if (tabsRef.current) observer.unobserve(tabsRef.current)
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Top Section Grid: Title (left) and New Paragraph (right) */}
        <div className="intro-grid">
          <div className="header-title-wrapper animate-left" ref={headerTitleRef}>
            <h2 className="header-title8188">
              OUR
              <br />
              <span className="highlight">DATA SCIENCE</span>
              <br />
              <span className="highlight">PROCESS</span>
            </h2>
          </div>
          <div className="top-right-paragraph animate-left" ref={topParagraphRef}>
            <p>
              Using the ideal processes to fit your needs.We follow a technology-agnostic process, designing AI systems through incremental development and continuous value delivery using the scrum methodology.
            </p>
          </div>

          {/* Main Content Grid: Long Description (left) and Image (right) */}
          <div className="main-description-wrapper animate-left" ref={mainDescriptionRef}>
            <p className="main-description">
              The process starts with a deep dive into the business vision, researching existing solutions and available data to allow for a clear problem definition.

              <br />
              It is followed by a comprehensive analysis of the gathered data and ideation of possible solutions, leading to a proof of concept and viability assessment.
              <br />
              After the prototyped solution is integrated into the product, a streamlined MLOps flow is set in place for our data scientist to run quick experiments and production increments.
              <br/>
              Throughout the whole process, the High-Level Architecture of the AI solution and the Project Plan are designed and refined, accounting all requirements, dependencies, costs and risks.
            </p>
          </div>
          <div className="image-section animate-right" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "research" ? "active" : ""}`}
              onClick={() => setActiveTab("research")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              RESEARCH
            </button>
            <button
              className={`tab-button ${activeTab === "ideation" ? "active" : ""}`}
              onClick={() => setActiveTab("ideation")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <line x1="12" y1="7" x2="12" y2="17" />
                  <line x1="7" y1="12" x2="17" y2="12" />
                </svg>
              </div>
              IDEATION
            </button>
            <button
              className={`tab-button ${activeTab === "execution" ? "active" : ""}`}
              onClick={() => setActiveTab("execution")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              EXECUTION
            </button>
            <button
              className={`tab-button ${activeTab === "technical_assessment" ? "active" : ""}`}
              onClick={() => setActiveTab("technical_assessment")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 14v4" />
                  <path d="M12 6v2" />
                  <path d="M12 2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                  <path d="M12 10a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />
                  <path d="M12 18a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />
                </svg>
              </div>
              TECHNICAL ASSESSMENT
            </button>
          </div>

          <div className="tab-content">
            {Object.keys(tabContent).map((key) => {
              const content = tabContent[key]
              return (
                <div key={key} className={`tab-panel ${activeTab === key ? "active" : ""}`}>
                  <h3 className="tab-title">{content.title}</h3>
                  {content.steps && (
                    <ul className="tab-list8188">
                      {content.steps.map((item, index) => (
                        <li key={index} className="tab-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {content.paragraphs && (
                    <>
                      {content.paragraphs.map((paragraph, index) => (
                        <p key={index} className="tab-description">
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ProductDesignProcess
